// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TYPE_CONFIG } from "./constants";

const DOW_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function truncateTitle(title, max = 14) {
  return title.length > max ? `${title.slice(0, max - 1)}…` : title;
}

function formatDateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const AvailabilityCalendar = ({
  currentMonth,
  filteredData,
  onMonthChange,
  onDayClick,
}) => {
  const { monthName, cells } = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    const monthLabel = currentMonth.toLocaleDateString("de-DE", {
      month: "long",
      year: "numeric",
    });

    const dateMap = {};
    filteredData.forEach((d) => {
      if (!dateMap[d.date]) dateMap[d.date] = [];
      dateMap[d.date].push(d);
    });

    const today = new Date();
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;

    const gridCells = [];

    for (let i = 0; i < startDow; i++) {
      const prevDate = new Date(y, m, -startDow + i + 1);
      gridCells.push({
        key: `prev-${i}`,
        day: prevDate.getDate(),
        otherMonth: true,
        dateStr: null,
        items: [],
        isToday: false,
      });
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = formatDateKey(y, m, d);
      const items = dateMap[dateStr] || [];
      const isToday =
        today.getFullYear() === y &&
        today.getMonth() === m &&
        today.getDate() === d;
      gridCells.push({
        key: dateStr,
        day: d,
        otherMonth: false,
        dateStr,
        items,
        isToday,
      });
    }

    const totalCells = startDow + lastDay.getDate();
    const remainder = totalCells % 7;
    if (remainder !== 0) {
      for (let i = 1; i <= 7 - remainder; i++) {
        gridCells.push({
          key: `next-${i}`,
          day: i,
          otherMonth: true,
          dateStr: null,
          items: [],
          isToday: false,
        });
      }
    }

    return { monthName: monthLabel, cells: gridCells };
  }, [currentMonth, filteredData]);

  return (
    <div className="av-cal-wrap">
      <div className="av-cal-nav">
        <button
          type="button"
          className="av-cal-nav-btn"
          onClick={() => onMonthChange(-1)}
          aria-label="Vorheriger Monat"
        >
          <ChevronLeft size={16} aria-hidden />
        </button>
        <h2 className="av-cal-nav-title">{monthName}</h2>
        <button
          type="button"
          className="av-cal-nav-btn"
          onClick={() => onMonthChange(1)}
          aria-label="Nächster Monat"
        >
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>
      <div
        className="av-cal-grid"
        role="grid"
        aria-label={`Kalender ${monthName}`}
      >
        {DOW_LABELS.map((d) => (
          <div
            key={d}
            role="columnheader"
            aria-label={d}
            className="av-cal-dow"
          >
            {d}
          </div>
        ))}
        {cells.map((cell) => {
          const hasItems = cell.items.length > 0;
          const classNames = [
            "av-cal-cell",
            cell.otherMonth ? "other-month" : "",
            hasItems ? "has-items" : "",
          ]
            .filter(Boolean)
            .join(" ");

          const ariaLabel = cell.otherMonth
            ? undefined
            : `${cell.day}. ${monthName}${
                hasItems
                  ? `, ${cell.items.length} ${cell.items.length === 1 ? "Eintrag" : "Einträge"}`
                  : ""
              }`;

          return (
            <div
              key={cell.key}
              role="gridcell"
              aria-label={ariaLabel}
              aria-disabled={cell.otherMonth || undefined}
              className={classNames}
              tabIndex={hasItems ? 0 : -1}
              onClick={
                hasItems && cell.dateStr
                  ? () => onDayClick(cell.dateStr)
                  : undefined
              }
              onKeyDown={
                hasItems && cell.dateStr
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onDayClick(cell.dateStr);
                      }
                    }
                  : undefined
              }
            >
              <span className={`av-day-num${cell.isToday ? " today" : ""}`}>
                {cell.day}
              </span>
              <div className="av-cal-chips">
                {cell.items.slice(0, 3).map((item) => {
                  const cfg = TYPE_CONFIG[item.type];
                  const Icon = cfg.Icon;
                  return (
                    <div
                      key={item.id}
                      className="av-cal-chip"
                      style={{
                        background: cfg.bg,
                        color: cfg.text,
                        borderLeftColor: cfg.border,
                      }}
                      role="img"
                      aria-label={`${cfg.label}: ${item.title}`}
                    >
                      <Icon size={11} aria-hidden />
                      <span>{truncateTitle(item.title)}</span>
                      {item.source === "seas" && (
                        <span className="av-seas-badge">SEAS</span>
                      )}
                    </div>
                  );
                })}
                {cell.items.length > 3 && (
                  <div className="av-more-chip">
                    +{cell.items.length - 3} weitere
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
